import * as THREE from 'three';
import { bodiesOf } from './bodies';
import type { Body, BodyMetrics } from './bodies';
import { addHighlight, highlightMaterial } from './highlight';
import { whenIdle } from './idle';
import { disposePoints, markerPoints, showPoints } from './marks';
import { hoverColor } from './palette';
import { PerMesh } from './perMesh';
import { CLICK_SLOP_PX, modelHit } from './picking';
import { PointerClicks } from './pointerClicks';
import { SnapScope, snapEdges } from './snap';
import type { MeshEdges } from './snap';
import { VertexWelder } from './weld';

const VERTEX_PX = 10;
const DOUBLE_CLICK_MS = 400;

export type SelectionMode = 'body' | 'vertex';

export interface SelectedPart {
  index: number;
  count: number;
  metrics: BodyMetrics;
}

type Pick =
  | { kind: 'body'; key: string; body: Body; index: number }
  | { kind: 'vertex'; key: string; point: THREE.Vector3 };

interface Click {
  at: number;
  x: number;
  y: number;
}

const bodyOf = (pick: Pick | null | undefined): Body | null => (pick?.kind === 'body' ? pick.body : null);

/**
 * Highlights the body or corner under the pointer and selects the one clicked.
 * Shift-click adds or removes a corner; a double-click takes the body.
 */
export class PartSelection {
  /** Holds the corner highlights, in world space. */
  readonly object = new THREE.Group();
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer: PointerClicks;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly color: number;
  private readonly scope = new SnapScope();
  private readonly picks = new Map<string, Pick>();
  private readonly painted = new Map<string, () => void>();
  private mode: SelectionMode = 'body';
  private model: THREE.Object3D | null = null;
  private bodies: Body[] | null = null;
  private edges: PerMesh<MeshEdges> | null = null;
  private welder = new VertexWelder();
  private clip: THREE.Plane | null = null;
  private hovered: Pick | null = null;
  private lastClick: Click | null = null;
  private enabled = true;
  private cancelPrepare: (() => void) | null = null;

  constructor(
    private readonly element: HTMLElement,
    isDark: boolean,
    getCamera: () => THREE.Camera,
    isCovered: (event: PointerEvent) => boolean,
    private readonly onChange: (part: SelectedPart | null) => void,
  ) {
    this.material = highlightMaterial(isDark);
    this.color = hoverColor(isDark);
    this.pointer = new PointerClicks(
      element,
      isCovered,
      () => this.enabled,
      (rect, event) => this.click(getCamera(), rect.height, event),
      () => this.clearHover(),
    );
  }

  get body(): Body | null {
    return this.picks.size === 1 ? bodyOf(this.picks.values().next().value) : null;
  }

  get hoveredBody(): Body | null {
    return bodyOf(this.hovered);
  }

  get hasSelection(): boolean {
    return this.picks.size > 0;
  }

  setMode(mode: SelectionMode): void {
    if (mode === this.mode) return;
    this.clearHover();
    this.clear();
    this.edges?.stop();
    this.mode = mode;
    this.prepare();
    this.invalidate();
  }

  setModel(model: THREE.Object3D | null): void {
    this.clearHover();
    this.clear();
    this.cancelPrepare?.();
    this.edges?.stop();
    this.model = model;
    this.bodies = null;
    this.welder = new VertexWelder();
    this.edges = model ? new PerMesh(model, snapEdges(model)) : null;
    this.cancelPrepare = model ? whenIdle(() => this.bodiesOfModel()) : null;
    this.prepare();
  }

  setClip(plane: THREE.Plane | null): void {
    this.clip = plane;
    this.material.clippingPlanes = plane ? [plane] : null;
    this.repaint();
    this.invalidate();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.pointer.cancelPress();
    if (enabled) this.invalidate();
    else this.clearHover();
  }

  /** The view moved under a still pointer. */
  invalidate(): void {
    this.pointer.invalidate();
  }

  update(camera: THREE.Camera): void {
    if (!this.pointer.takeStale()) return;
    this.hover(this.enabled && this.pointer.hovering ? this.pickAt(camera, this.mode) : null);
  }

  clearHover(): void {
    this.hover(null);
  }

  clear(): void {
    if (this.picks.size === 0) return;
    this.picks.clear();
    this.paint();
    if (this.mode === 'body') this.onChange(null);
  }

  dispose(): void {
    this.cancelPrepare?.();
    this.edges?.stop();
    this.pointer.dispose();
    this.picks.clear();
    this.hovered = null;
    this.paint();
    this.material.dispose();
    this.object.removeFromParent();
  }

  private prepare(): void {
    if (this.mode === 'vertex') this.edges?.prepare();
  }

  private paint(): void {
    this.painted.forEach((remove, key) => {
      if (this.picks.has(key) || this.hovered?.key === key) return;
      remove();
      this.painted.delete(key);
    });
    this.picks.forEach((pick) => this.draw(pick));
    if (this.hovered) this.draw(this.hovered);
  }

  private repaint(): void {
    this.painted.forEach((remove) => remove());
    this.painted.clear();
    this.paint();
  }

  private draw(pick: Pick): void {
    if (!this.painted.has(pick.key)) this.painted.set(pick.key, this.highlight(pick));
  }

  private highlight(pick: Pick): () => void {
    switch (pick.kind) {
      case 'body': {
        const overlays = pick.body.meshes.map((mesh) => addHighlight(mesh, this.material));
        return () => overlays.forEach((overlay) => overlay.removeFromParent());
      }
      case 'vertex': {
        const marker = markerPoints(this.color, VERTEX_PX, 1);
        showPoints(marker, [pick.point]);
        marker.material.clippingPlanes = this.clip ? [this.clip] : null;
        this.object.add(marker);
        return () => disposePoints(marker);
      }
    }
  }

  private hover(pick: Pick | null): void {
    if (pick?.key === this.hovered?.key) return;
    this.hovered = pick;
    this.paint();
  }

  private pickAt(camera: THREE.Camera, mode: SelectionMode, heightPx?: number): Pick | null {
    if (!this.model) return null;
    this.raycaster.setFromCamera(this.pointer.ndc, camera);
    const hit = modelHit(this.raycaster, this.model, this.clip);
    if (!hit) return null;
    const mesh = hit.object as THREE.Mesh;
    if (mode === 'body') return this.bodyPick(mesh);
    return this.vertexPick(hit, camera, heightPx ?? this.element.getBoundingClientRect().height);
  }

  private bodyPick(mesh: THREE.Mesh): Pick | null {
    const bodies = this.bodiesOfModel();
    const index = bodies.findIndex((body) => body.has(mesh));
    return index < 0 ? null : { kind: 'body', key: `body ${index}`, body: bodies[index], index };
  }

  private vertexPick(hit: THREE.Intersection, camera: THREE.Camera, heightPx: number): Pick | null {
    if (!this.edges) return null;
    const edges = this.edges.of(hit.object as THREE.Mesh);
    const { ray } = this.raycaster;
    const { reach, accept } = this.scope.around(hit, ray, camera, heightPx, this.clip);
    const point = edges.nearestCorner(ray, reach, accept);
    return point && { kind: 'vertex', key: `vertex ${this.welder.idOf(point)}`, point };
  }

  private bodiesOfModel(): Body[] {
    this.cancelPrepare?.();
    this.cancelPrepare = null;
    this.bodies ??= this.model ? bodiesOf(this.model) : [];
    return this.bodies;
  }

  private click(camera: THREE.Camera, heightPx: number, event: PointerEvent): void {
    const double = this.isDoubleClick(event);
    if (this.mode === 'body') {
      this.select(this.pickAt(camera, 'body'));
      return;
    }
    const pick = this.pickAt(camera, double ? 'body' : this.mode, heightPx);
    if (double || !event.shiftKey) this.replace(pick);
    else if (pick) this.toggle(pick);
  }

  private isDoubleClick(event: PointerEvent): boolean {
    const last = this.lastClick;
    const double =
      last !== null &&
      event.timeStamp - last.at <= DOUBLE_CLICK_MS &&
      Math.hypot(event.clientX - last.x, event.clientY - last.y) <= CLICK_SLOP_PX;
    this.lastClick = double ? null : { at: event.timeStamp, x: event.clientX, y: event.clientY };
    return double;
  }

  private replace(pick: Pick | null): void {
    this.picks.clear();
    if (pick) this.picks.set(pick.key, pick);
    this.paint();
  }

  private toggle(pick: Pick): void {
    if (!this.picks.delete(pick.key)) this.picks.set(pick.key, pick);
    this.paint();
  }

  private select(pick: Pick | null): void {
    if (pick?.kind !== 'body' || !this.bodies) {
      this.clear();
      return;
    }
    if (this.picks.has(pick.key)) return;
    this.replace(pick);
    this.onChange({ index: pick.index, count: this.bodies.length, metrics: pick.body.metrics() });
  }
}
